import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Organization } from '@b3networks/api/auth';
import { Page, Pageable } from '@b3networks/api/common';
import { SkuModel } from '@b3networks/api/store';
import { randomGuid, X_B3_HEADER, X_PAGINATION } from '@b3networks/shared/common';
import { HashMap } from '@datorama/akita';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import {
  AddRequest,
  AssignNumberRequest,
  B3Number,
  ByoiNumber,
  ByoiVendors,
  CheckDocumentResult,
  FindNumberReq,
  MultiReserveResp,
  NumberModel,
  ReserveNumberRequest,
  UpdateAttributesRequest
} from './number.model';
import { NumberStore } from './number.store';

@Injectable({ providedIn: 'root' })
export class NumberService {
  constructor(
    private numberStore: NumberStore,
    private http: HttpClient
  ) {}

  findNumbers(req: FindNumberReq, pageable?: Pageable, promotedOrgUuid?: string): Observable<Page<B3Number>> {
    let headers = new HttpHeaders();
    if (promotedOrgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, promotedOrgUuid);
    }
    req = Object.assign(new FindNumberReq(), req) || new FindNumberReq();

    let params = req.toParams();
    if (pageable) {
      params = params.set('page', String(pageable.page)).set('perPage', String(pageable.perPage));
    }

    return this.http
      .get<B3Number[]>(`number/private/v2/numbers`, { headers: headers, params: params, observe: 'response' })
      .pipe(
        map(resp => {
          const page = new Page<B3Number>();
          page.totalCount = +resp.headers.get(X_PAGINATION.totalCount);
          page.content = resp.body.map(num => new B3Number(num));
          return page;
        }),
        tap(page => this.numberStore.set(page.content))
      );
  }

  setActive(numbers: B3Number[]) {
    this.numberStore.setActive(numbers.map(n => n.number));
  }

  makeActive(number: B3Number) {
    if (!this.numberStore.getValue().active) {
      this.numberStore.setActive([number.number]);
    } else {
      this.numberStore.addActive(number.number);
    }
  }

  removeActive(number: B3Number) {
    this.numberStore.removeActive(number.number);
  }

  getSkus(productId: string) {
    return this.http.get<string[]>(`number/public/v1/skus`, { params: { productId } });
  }

  checkDocument(numberSku: string, orgUuid: string) {
    let params = new HttpParams();
    params = params.set('skus', numberSku);
    params = params.set('orgUuid', orgUuid);

    return this.http.post<CheckDocumentResult[]>(`number/private/v3/check-document`, {}, { params });
  }

  // Store
  fetchPricingCodes(productId: string, voiceMode: string) {
    let params = new HttpParams();

    params = params.set('productCode', productId);
    params = params.set('capability', voiceMode);
    params = params.set('page', '1');
    params = params.set('perPage', '1000');

    return this.http.get(`number/private/v1/pricingCodes`, { params });
  }

  getPricingCodes(productId: string, capability: string, numberState?: string): Observable<string[]> {
    const numberStateCondition = numberState ? `&numberState=${numberState}` : '';

    return this.http.get<string[]>(
      `/number/private/v1/pricingCodes?productCode=${productId}&page=1&perPage=10000000&capability=${capability}${numberStateCondition}`
    );
  }

  assignNumber(number: string, assignNumberRequest: AssignNumberRequest): Observable<any> {
    return this.http.put(`/number/private/v1/numbers/${number}`, assignNumberRequest);
  }

  assignNumberV3(number: string, subscriptionUuid: string, orgUuid?: string): Observable<any> {
    const url = `/number/private/v3/numbers/${number}/assign`;
    const body = {
      subscriptionUuid
    };

    return this.http.put<any>(url, body, {
      headers: orgUuid ? new HttpHeaders().set('x-user-org-uuid', orgUuid) : null
    });
  }

  assignMultipleNumbers(orgId: string, numbers: B3Number[], subscriptionUuid: string) {
    const url = `/number/private/v5/numbers/assign`;
    const body = {
      numbers: numbers.map(x => x.number),
      subscriptionUuid
    };

    return this.http.put(url, body, {
      headers: new HttpHeaders().set('x-user-org-uuid', orgId).set('x-idempotency-key', randomGuid())
    });
  }

  removeNumber(number: string, orgUuid: string) {
    let headers = new HttpHeaders();
    const url = `/number/private/v3/numbers/${number}/unassign`;

    if (orgUuid) {
      headers = headers.set('x-user-org-uuid', orgUuid);
    }

    return this.http.put(url, {}, { headers: headers });
  }

  reserveNumber(number: string, reserveNumberRequest: ReserveNumberRequest): Observable<any> {
    return this.http.put(`/number/private/v1/numbers/${number}`, reserveNumberRequest);
  }

  reserveNumberV3(number: string, orgUuid: string): Observable<any> {
    return this.http.put(`/number/private/v3/numbers/${number}/reserve`, { orgUuid });
  }

  checkMultiDocument(skus: string[], orgUuid: string): Observable<CheckDocumentResult[]> {
    return this.http.post<CheckDocumentResult[]>(
      `/number/private/v3/check-document?skus=${skus.join(',')}&orgUuid=${orgUuid}`,
      {}
    );
  }

  calcActivationFee(numbers: string[], orgUuid?: string): Observable<number> {
    return this.http.post<number>(
      `/number/private/v2/numbers/calcOTC`,
      { numbers },
      {
        headers: orgUuid ? new HttpHeaders().set('x-user-org-uuid', orgUuid) : null
      }
    );
  }

  reserveMultipleNumbers(orgUuid: string, numbers: string[]): Observable<MultiReserveResp> {
    const body = {
      numbers: numbers.join(','),
      orgUuid
    };

    return this.http.put<MultiReserveResp>(`/number/private/v3/numbers/reserve`, body, {
      headers: new HttpHeaders().set('x-user-org-uuid', orgUuid)
    });
  }

  reserveMultipleNumbersWithReponseHeader(orgUuid: string, numbers: string[]): Observable<HttpResponse<any>> {
    const url = `/number/private/v3/numbers/reserve`;
    const body = {
      numbers: numbers.join(','),
      orgUuid
    };
    return this.http.put<any>(url, body, {
      headers: new HttpHeaders().set('x-user-org-uuid', orgUuid),
      observe: 'response'
    });
  }

  getPricingSkuCodes(orgId: string, productId: string, capabilities?: string): Observable<string[]> {
    let params = new HttpParams();
    if (productId) params = params.set('productId', productId);

    if (capabilities) params = params.append('capabilities', String(capabilities));

    return this.http.get<string[]>('/number/private/v2/domains/skus', {
      headers: new HttpHeaders().set('x-user-org-uuid', orgId),
      params: params
    });
  }

  loadAllPurchasableNumbers(sku: string, orgUuid: string, capabilities: string): Observable<B3Number[]> {
    const body: B3Number[] = [];
    const page = 1;
    const perPage = 100;

    const getPurchasableNumbers = (page: number) =>
      this.getPurchasableNumbers(sku, orgUuid, capabilities, page, perPage, '', true);

    const fetchPage = (page: number): Observable<B3Number[]> =>
      getPurchasableNumbers(page).pipe(
        concatMap(response => {
          const numbers = response?.['content'] || [];
          body.push(...numbers);

          if (numbers.length === perPage) {
            return fetchPage(page + 1);
          } else {
            return of(body);
          }
        }),
        catchError(error => {
          console.error('Error fetching page', error);
          return of(body);
        })
      );

    return fetchPage(page).pipe(
      tap(res => {
        const numberMapping: HashMap<B3Number[]> = {};
        numberMapping[sku] = res;
        this.numberStore.update(state => {
          const updatedNumberSkus = {
            ...state.numberSkus,
            ...numberMapping
          };
          return { ...state, numberSkus: updatedNumberSkus };
        });
      })
    );
  }

  getPurchasableNumbers(
    sku: string,
    buyerUuid: string,
    capabilities: string,
    page: number,
    perPage: number,
    keyword?: string,
    getPage = false
  ): Observable<Page<B3Number> | B3Number[]> {
    let params = new HttpParams()
      .set('sku', sku)
      .set('buyerUuid', buyerUuid)
      .set('capabilities', capabilities)
      .set('page', String(page))
      .set('perPage', String(perPage));

    if (keyword) {
      params = params.set('keyword', keyword);
    }

    return this.http
      .get<Page<B3Number> | B3Number[]>('/number/private/v3/numbers/purchasable', { params, observe: 'response' })
      .pipe(
        map(res => {
          if (getPage) {
            const page = new Page<B3Number>();
            page.content = res.body as B3Number[];
            page.totalCount = +res.headers.get(X_PAGINATION.totalCount);
            return page;
          } else {
            return res.body;
          }
        })
      );
  }

  swapNumber(number: string, selectedNumber: string, orgUuid: string) {
    let headers = new HttpHeaders();

    if (orgUuid) {
      headers = headers.set('x-user-org-uuid', orgUuid);
    }

    const url = `/number/private/v2/numbers/${number}`;
    const body = {
      action: 'switch_number',
      data: {
        destinationNumber: selectedNumber
      }
    };

    return this.http.put(url, body, { headers: headers });
  }

  getNumbers(
    state: string,
    page: number = 1,
    pageSize: number = 10,
    filters: any = {}
  ): Observable<HttpResponse<NumberModel[]>> {
    let params = new HttpParams().set('page', String(page)).set('perPage', String(pageSize));

    if (state) {
      params = params.set('states', state);
    }

    for (const field of Object.keys(filters)) {
      if (filters[field] != null) {
        params = params.set(field, encodeURI(filters[field]));
      }
    }

    return this.http.get<NumberModel[]>('/number/private/admin/v3/numbers', { params, observe: 'response' });
  }

  getDomainsSkus(): Observable<SkuModel[]> {
    return this.http
      .get<SkuModel[]>(`/number/private/admin/v3/domains/skus`)
      .pipe(map(res => res.sort((a, b) => a.sku.toLowerCase().localeCompare(b.sku.toLowerCase()))));
  }

  reserve(numberStr: string, body: any): Observable<void> {
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/reserve`, body);
  }

  extendReservation(numberStr: string, subscriptionUuid: string): Observable<void> {
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/extend-reservation`, {
      subscriptionUuid
    });
  }

  freeExtendReservation(numberStr: string, expirationDate: string): Observable<void> {
    return this.http.put<void>(`/number/private/v3/numbers/${numberStr}/free-extend-reservation`, { expirationDate });
  }

  lock(numberStr: string, expiryDate: string, remark: string): Observable<void> {
    const body = { remark, expiryDate };
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/lock`, body);
  }

  unlock(numberStr: string, remark: string): Observable<void> {
    const body = { remark };
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/unlock`, body);
  }

  assign(numberStr: string, body: any): Observable<void> {
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/assign`, body);
  }

  unassign(numberStr: string, body: any): Observable<void> {
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/unassign`, body);
  }

  release(numberStr: string, remark: string): Observable<void> {
    const body = { remark };
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/release`, body);
  }

  transfer(numberStr: string, newOwnerOrgUuid: string, remark: string): Observable<any> {
    const body = { orgUuid: newOwnerOrgUuid, remark };
    return this.http.put<any>(`/number/private/admin/v3/numbers/${numberStr}/transfer`, body);
  }

  getByoiVendors(): Observable<ByoiVendors[]> {
    return this.http.get<ByoiVendors[]>(`/number/private/v3/vendors/byoi`);
  }

  remove(numberStr: string, remark: string): Observable<void> {
    const body = { remark };
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/delete`, body);
  }

  recover(numberStr: string, remark: string): Observable<void> {
    const body = { remark };
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/recover`, body);
  }

  getStaleAvailableNumbers(page = 1, perPage = 10): Observable<HttpResponse<NumberModel[]>> {
    const params = new HttpParams().set('page', String(page)).set('perPage', String(perPage));
    return this.http.get<NumberModel[]>(`/number/private/admin/v3/stale-numbers`, { params, observe: 'response' });
  }

  exportNumbers(state: string, filters) {
    let params = new HttpParams();

    if (state) {
      params = params.set('states', state);
    }

    for (const field of Object.keys(filters)) {
      if (filters[field] != null) {
        params = params.set(field, encodeURI(filters[field]));
      }
    }

    return this.http.get(`/number/private/admin/v3/numbers/export`, { params, responseType: 'text' });
  }

  exportStaleNumbers(startDate: string, endDate: string) {
    return this.http.post(
      `/number/private/admin/v3/stale-numbers/export`,
      {
        startDate,
        endDate
      },
      { responseType: 'text' }
    );
  }

  addNote(numberStr: string, note: string): Observable<void> {
    return this.http.put<void>(`/number/private/admin/v3/numbers/${numberStr}/note`, { note });
  }

  addNumbers(formData: FormData): Observable<void> {
    return this.http.post<void>(`/number/private/admin/v2/numbers/csv`, formData);
  }

  isByoiPartner(): Observable<boolean> {
    return this.http.get<boolean>(`/number/private/v3/byoi/verify`).pipe(
      catchError(_ => {
        console.log('Error verify byoi partner!');
        return of(false);
      })
    );
  }

  getByoiNumbers(pageable: Pageable, status: string, filters = {}): Observable<Page<ByoiNumber>> {
    let params = new HttpParams().set('page', String(pageable.page)).set('size', String(pageable.perPage));

    if (status) {
      params = params.set('state', status);
    }

    for (const field of Object.keys(filters)) {
      if (filters[field]) {
        params = params.set(field, filters[field]);
      }
    }

    return this.http.get<ByoiNumber[]>(`/number/private/v3/numbers/byoi`, { observe: 'response', params }).pipe(
      map(res => {
        const result = new Page<ByoiNumber>();
        result.content = res.body?.['content'].map(txn => new ByoiNumber(txn));
        result.totalCount = +res.body?.['totalElements'];
        return result;
      })
    );
  }

  exportByoiNumbers(status: string, filters = {}) {
    let params = new HttpParams();

    if (status) {
      params = params.set('state', status);
    }

    for (const field of Object.keys(filters)) {
      if (filters[field]) {
        params = params.set(field, filters[field]);
      }
    }

    return this.http.get(`/number/private/v3/numbers/byoi/export`, { params, responseType: 'text' });
  }

  getHistory(numberStr: string): Observable<string[]> {
    return this.http.get<string[]>(`/number/private/admin/v2/numbers/${numberStr}/history`);
  }

  getOrgs(orgUuids: string[]): Observable<Organization[]> {
    return this.http.get<Organization[]>(`/number/private/admin/v3/org?uuids=${orgUuids.join(',')}`);
  }

  addNumber(req: AddRequest): Observable<void> {
    return this.http.post<void>(`/number/private/v3/numbers/byoi`, req);
  }

  visibleDomain(numberStr: string, visibleToDomain: string, remake: string): Observable<void> {
    const body = {
      action: 'update_attributes',
      data: { visibleToDomain },
      remake
    };
    return this.http.put<void>(`/number/private/admin/v2/numbers/${numberStr}`, body);
  }

  getPartnerVendors(): Observable<ByoiVendors[]> {
    return this.http.get<ByoiVendors[]>(`/number/private/v3/vendors/byoi`);
  }

  updateAttributes(number: string, req: UpdateAttributesRequest) {
    const body = {
      action: 'update_attributes',
      data: req
    };
    return this.http.put(`/number/private/v2/numbers/${number}`, body);
  }

  getNumbersByProductId(productId: string): Observable<string[]> {
    return this.http.get<string[]>(`/number/private/v2/domains/skus`, { params: { productId } });
  }
}
